/* ===========================================================================
 * cart-upsell.js — progressive enhancement for the SERVER-RENDERED cart upsell
 * (snippets/cart-upsell.liquid). The cards are already in the drawer HTML, so
 * there is nothing to render or repopulate here — that's what kills the blink /
 * disappear-on-qty-change. This only:
 *   1. flavour <select> change → swap the card's price/compare/image + retarget
 *      the "+" button to the chosen variant.
 *   2. "+" click → /cart/add.js (variant's own price; no discount logic) then
 *      refresh the drawer section. Emits cart:updated so upsell-blocker.js et al.
 *      stay in sync.
 * All handlers are DELEGATED on document, so they keep working after Dawn swaps
 * #CartDrawer. Adds carry _upsell_placement:'cart' + _upsell_id line props.
 * =========================================================================== */
(function () {
  'use strict';
  if (window.__tdCartUpsell) return;
  window.__tdCartUpsell = true;

  function root() {
    return (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/');
  }
  function log() {
    if (window.TD_UPSELL_DEBUG && window.console) {
      console.log.apply(console, ['[cart-upsell]'].concat([].slice.call(arguments)));
    }
  }

  // --- 1. Variant <select> change: update price / compare / image / add target.
  // Capture phase + stopPropagation so Dawn's cart-items change handler (quantity
  // validation) never sees this select — a <select> has no `step`, which would
  // otherwise show "increments of undefined".
  document.addEventListener('change', function (e) {
    var sel = e.target.closest ? e.target.closest('[data-cu-select]') : null;
    if (!sel) return;
    e.stopPropagation();
    var card = sel.closest('.td-cart-upsell__card');
    if (!card) return;
    var opt = sel.options[sel.selectedIndex];
    if (!opt) return;

    var priceRow = card.querySelector('[data-cu-pricerow]');
    if (priceRow) {
      var compare = opt.getAttribute('data-compare');
      var price = opt.getAttribute('data-price');
      priceRow.innerHTML =
        '<span class="td-cart-upsell__price">' + (price || '') + '</span>' +
        (compare ? '<span class="td-cart-upsell__compare">' + compare + '</span>' : '');
    }
    var img = card.querySelector('.td-cart-upsell__img');
    var newImg = opt.getAttribute('data-image');
    if (img && newImg) img.src = newImg;

    // Swap the dropdown's flavour icon to the selected variant's.
    var thumb = opt.getAttribute('data-thumb');
    if (thumb) {
      sel.style.setProperty('--cu-thumb', "url('" + thumb + "')");
      sel.setAttribute('data-cu-has-thumb', 'true');
    }

    var addBtn = card.querySelector('[data-cu-add]');
    if (addBtn) {
      addBtn.setAttribute('data-cu-variant', opt.value);
      addBtn.setAttribute('data-cu-variant-title', opt.text || opt.textContent || '');
    }
  }, true);

  // --- 2. "+" click: add the chosen variant, then refresh the drawer.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest('[data-cu-add]') : null;
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('data-adding') === '1') return; // guard against double-clicks
    var variantId = parseInt(btn.getAttribute('data-cu-variant'), 10);
    if (!variantId) return;
    var upsellId = btn.getAttribute('data-cu-id') || '';
    var slot = btn.getAttribute('data-cu-slot') || '';
    var variantTitle = btn.getAttribute('data-cu-variant-title') || '';
    var card = btn.closest('.td-cart-upsell__card');
    // Accessories rows (snippets/cart-accessories.liquid) carry data-cu-block.
    var block = card ? card.getAttribute('data-cu-block') || '' : '';
    btn.setAttribute('data-adding', '1');
    if (card) card.classList.add('is-adding');

    // Single request: /cart/add.js renders the sections we need and returns them,
    // so we don't do a second round-trip for the drawer HTML.
    //
    // The upsell popup wraps window.fetch and fires on ANY /cart/add.js. This add
    // comes from the drawer's own upsell rail, with the drawer already open, so
    // the popup must stay shut. The flag is read synchronously inside that wrapper
    // (both at call time and in its response handler), which is why it is lowered
    // only once the request has settled, not on the next line.
    window.__tdSuppressUpsellPopup = true;
    var props = {
      _upsell_placement: 'cart',
      _upsell_id: upsellId,
      _upsell_slot: slot,
      _upsell_variant: variantTitle,
      _upsell_discount: 'none'
    };
    if (block) props._upsell_block = block;
    // Fixed bundle card (snippets/cart-upsell.liquid): same Flavors/_FlavorIds the
    // popup sends, so the Cart Transform can expand the paid part of the pack.
    var flavorIds = card ? card.getAttribute('data-cu-flavor-ids') || '' : '';
    if (flavorIds) {
      props.Flavors = card.getAttribute('data-cu-flavors') || '';
      props._FlavorIds = flavorIds;
    }
    fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: props
        }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) {
        return r.json().catch(function () { return null; }).then(function (body) {
          // /cart/add.js answers 422 with {status, message, description} when the
          // line cannot be added. The old code read the body without checking, so a
          // sold-out response took the success path and swapped in stale sections.
          if (!r.ok || (body && body.status)) {
            var err = new Error((body && (body.description || body.message)) || 'Cart add failed');
            err.cartBody = body;
            err.httpStatus = r.status;
            throw err;
          }
          return body;
        });
      })
      .then(function (data) {
        log('added', variantId, upsellId);
        trackAdd('add_success', card, btn, { data: data });
        applySections(data && data.sections);
      })
      .catch(function (err) {
        log('add failed', err);
        trackAdd('add_error', card, btn, { error: err });
      })
      .then(function () {
        window.__tdSuppressUpsellPopup = false;
        btn.removeAttribute('data-adding');
        if (card) card.classList.remove('is-adding');
      });
  });

  // Swap in the sections returned by /cart/add.js (no extra fetch).
  function applySections(sections) {
    // The drawer runtime renders with sequencing + scroll preservation; it has
    // already applied this response through its fetch hook, so this is a no-op there.
    if (window.TDCart) { window.TDCart.applySections(sections); return; }
    var drawer = document.querySelector('cart-drawer');
    if (sections && sections['cart-drawer'] && drawer) {
      var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var fresh = doc.querySelector('#CartDrawer');
      var current = drawer.querySelector('#CartDrawer');
      // innerHTML swap, not replaceWith: keep the #CartDrawer node so Dawn's
      // open() transitionend (getElementById('CartDrawer')) never hits null.
      if (fresh && current) current.innerHTML = fresh.innerHTML;
      if (drawer.classList) drawer.classList.remove('is-empty');
    }
    if (sections && sections['cart-icon-bubble']) {
      var bubble = document.querySelector('#cart-icon-bubble');
      if (bubble) {
        var bdoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        var bfresh = bdoc.querySelector('.shopify-section') || bdoc.body.firstElementChild;
        if (bfresh) bubble.innerHTML = bfresh.innerHTML;
      }
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
  }

  // --- 3. Slider scroll indicator ------------------------------------------
  // The dots container is server-rendered empty; how MANY dots there should be
  // depends on measured widths, so it is filled in here. The slider snaps
  // card-by-card (scroll-snap-align: start on each card), so the number of
  // distinct resting positions is how far the grid can travel divided by one
  // card+gap — with 5 cards and 3 visible that gives 3 dots, matching the design.
  function gridFor(dots) {
    return dots.parentElement ? dots.parentElement.querySelector('.td-cart-upsell__grid') : null;
  }

  function syncDots(dots) {
    var grid = gridFor(dots);
    if (!grid) return;
    var maxScroll = grid.scrollWidth - grid.clientWidth;
    var card = grid.querySelector('.td-cart-upsell__card');
    // Nothing to scroll (desktop with few cards, empty-cart 2-col grid) → stay hidden.
    if (maxScroll <= 1 || !card) {
      dots.hidden = true;
      dots.innerHTML = '';
      return;
    }
    var cs = getComputedStyle(grid);
    var gap = parseFloat(cs.columnGap || cs.gap) || 0;
    var step = card.getBoundingClientRect().width + gap;
    if (step <= 0) { dots.hidden = true; return; }

    var count = Math.round(maxScroll / step) + 1;
    if (count < 2) { dots.hidden = true; dots.innerHTML = ''; return; }

    if (dots.children.length !== count) {
      var html = '';
      // Buttons, not spans — they scroll the slider on click. The label is the
      // position number, which needs no translation.
      for (var i = 0; i < count; i++) {
        html += '<button type="button" class="td-cart-upsell__dot" aria-label="' + (i + 1) + '"></button>';
      }
      dots.innerHTML = html;
    }
    setActive(dots, Math.round(grid.scrollLeft / step));
    dots.hidden = false;
  }

  // Kept separate so the click handler can mark the target dot immediately,
  // rather than waiting on a scroll event that a smooth scroll only delivers
  // once the animation has run.
  function setActive(dots, active) {
    var n = dots.children.length;
    if (active < 0) active = 0;
    if (active > n - 1) active = n - 1;
    for (var j = 0; j < n; j++) {
      dots.children[j].classList.toggle('is-active', j === active);
    }
  }

  // One snap step = one card + one gap. Used by the dots and the drag release.
  function stepOf(grid) {
    var card = grid.querySelector('.td-cart-upsell__card');
    if (!card) return 0;
    var cs = getComputedStyle(grid);
    var gap = parseFloat(cs.columnGap || cs.gap) || 0;
    return card.getBoundingClientRect().width + gap;
  }

  // Click a dot → scroll to that snap stop.
  function bindDotClicks(dots) {
    if (dots.__tdDotsClick) return;
    dots.__tdDotsClick = true;
    dots.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('.td-cart-upsell__dot') : null;
      if (!b) return;
      var grid = gridFor(dots);
      if (!grid) return;
      var step = stepOf(grid);
      if (!step) return;
      var idx = [].indexOf.call(dots.children, b);
      // Plain assignment, not scrollTo({behavior:'smooth'}) — the easing is done
      // in CSS via scroll-behavior on the grid, which animates this same
      // assignment and honours prefers-reduced-motion for free.
      grid.scrollLeft = idx * step;
      setActive(dots, idx);
    });
  }

  // Drag the slider with the mouse. Touch already scrolls natively, so this is
  // pointerType 'mouse' only — binding it for touch would fight the native
  // momentum scrolling.
  function bindDrag(grid, dots) {
    if (grid.__tdDragBound) return;
    grid.__tdDragBound = true;
    var down = false, startX = 0, startLeft = 0, travelled = 0;

    grid.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      travelled = 0;
      startX = e.clientX;
      startLeft = grid.scrollLeft;
      grid.classList.add('is-dragging');
    });

    grid.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > travelled) travelled = Math.abs(dx);
      grid.scrollLeft = startLeft - dx;
      if (dots) syncDots(dots);
      if (travelled > 3) e.preventDefault();
    });

    function end() {
      if (!down) return;
      down = false;
      grid.classList.remove('is-dragging');
      if (dots) syncDots(dots);
    }
    ['pointerup', 'pointercancel', 'pointerleave'].forEach(function (ev) {
      grid.addEventListener(ev, end);
    });

    // A drag finishes with a click on whatever sits under the cursor. Swallow it
    // so dragging across a card never fires that card's ADD button.
    grid.addEventListener('click', function (e) {
      if (travelled > 4) {
        e.preventDefault();
        e.stopPropagation();
        travelled = 0;
      }
    }, true);

    // Native drag of the product images would hijack the gesture.
    grid.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  function initDots() {
    var all = document.querySelectorAll('[data-cu-dots]');
    for (var i = 0; i < all.length; i++) {
      (function (dots) {
        syncDots(dots);
        bindDotClicks(dots);
        var grid = gridFor(dots);
        // Re-renders hand us a brand new grid node, so this flag never goes stale.
        if (!grid || grid.__tdDotsBound) return;
        grid.__tdDotsBound = true;
        bindDrag(grid, dots);
        // Updated synchronously rather than inside requestAnimationFrame: the work
        // is a handful of property reads and class toggles, and rAF only runs when
        // a frame is actually produced, which left the active dot a step behind.
        grid.addEventListener('scroll', function () { syncDots(dots); }, { passive: true });
      })(all[i]);
    }
    // The accessories cards scroll sideways too, with no dots.
    document.querySelectorAll('.td-cart-acc__list').forEach(function (list) { bindDrag(list, null); });
  }

  var dotsTimer = null;
  // A price row that does not fit on one line (long currency formats, narrow cards) drops
  // both prices to 11px via .is-tight. Measured at the normal size each time, so a wider
  // drawer or a shorter price goes back to normal.
  function fitPrices() {
    document.querySelectorAll('[data-cu-pricerow]').forEach(function (row) {
      row.classList.remove('is-tight');
      if (!row.clientWidth) return;
      var tight = row.scrollWidth > row.clientWidth + 1;
      row.querySelectorAll('.td-cart-upsell__price, .td-cart-upsell__compare').forEach(function (el) {
        var lh = parseFloat(getComputedStyle(el).lineHeight) || 0;
        if (lh && el.offsetHeight > lh * 1.4) tight = true;
      });
      if (tight) row.classList.add('is-tight');
    });
  }

  function scheduleDots() {
    if (dotsTimer) clearTimeout(dotsTimer);
    dotsTimer = setTimeout(function () { initDots(); fitPrices(); }, 60);
  }

  // Dawn's own cart re-render does not fire a cart event, so the observer is the
  // catch-all; the listeners just make the common paths respond faster.
  function startDots() {
    initDots();
    fitPrices();
    document.addEventListener('td-cart:open', function () { setTimeout(fitPrices, 320); });
    ['cart:updated', 'cart:refresh'].forEach(function (ev) {
      document.addEventListener(ev, scheduleDots);
    });
    window.addEventListener('resize', scheduleDots, { passive: true });
    var drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(scheduleDots).observe(drawer, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startDots);
  else startDots();

  // --- 4. Event tracking ----------------------------------------------------
  // Writes the SAME payload shape as assets/td-upsell-popup.js (see its track()):
  // 13 canonical keys, text/plain + no-cors + keepalive so the request stays a
  // "simple" CORS request and survives a page unload. Only `placement` differs
  // ('cart' vs 'popup'), plus a few cart-specific extras the receiver accepts.
  var SESSION_ID_KEY = 'td_upsell_popup_sid'; // deliberately shared with the popup,
                                              // so cart and popup events join on one session

  function sessionId() {
    try {
      var sid = sessionStorage.getItem(SESSION_ID_KEY);
      if (!sid) {
        sid = 's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem(SESSION_ID_KEY, sid);
      }
      return sid;
    } catch (e) {
      return 's_nostore';
    }
  }

  // Read fresh every time — the wrapper is replaced on every drawer re-render.
  // `el` picks the wrapper the card sits in: the slider ([data-td-cart-upsell]) or
  // the accessories list ([data-td-cart-acc]), which carries the same data.
  var WRAPPERS = '[data-td-cart-upsell], [data-td-cart-acc]';
  function ctx(el) {
    var w = (el && el.closest && el.closest(WRAPPERS)) || document.querySelector(WRAPPERS);
    if (!w) return null;
    var d = w.dataset;
    // Primary source is the theme setting `td_cart_upsell_endpoint`, rendered onto
    // the wrapper above — the same Liquid → markup → JS route the popup uses for
    // its own endpoint. The global is a dev-only override and is deliberately
    // cart-specific so it cannot collide with the popup's TD_UPSELL_TRACKING_ENDPOINT.
    // Blank setting and no override → return null → track() never runs. Silent, no error.
    var endpoint = window.TD_CART_UPSELL_TRACKING_ENDPOINT || d.cuEndpoint;
    if (!endpoint) return null;
    return {
      endpoint: endpoint,
      entry_handle: d.cuEntryHandle || '',
      entry_name: d.cuEntryName || '',
      trigger: d.cuTrigger || 'always',
      market: d.cuMarket || '',
      language: d.cuLanguage || '',
      page_type: d.cuPageType || 'other',
      page_handle: d.cuPageHandle || ''
    };
  }

  function track(event, c, extra) {
    try {
      if (!c || !c.endpoint) return;
      extra = extra || {};
      var body = {
        event: event,
        entry: extra.entry || c.entry_handle,
        internal_name: c.entry_name,
        placement: 'cart',
        market: c.market,
        page_type: c.page_type,
        page_handle: c.page_handle,
        page_path: window.location.pathname,
        fired_trigger: c.trigger,
        variant_id: extra.variant_id || null,
        value: extra.value || null,
        session_id: sessionId(),
        timestamp: Date.now(),
        // Which store the event came from. Without it DEV and LIVE rows are
        // indistinguishable in the sheet, since nothing else in the payload
        // identifies the shop. Dawn always sets window.Shopify.shop.
        shop: (window.Shopify && window.Shopify.shop) || ''
      };
      if (extra.reason) body.reason = extra.reason;
      if (extra.slot) body.slot = extra.slot;
      if (extra.variant_title) body.variant_title = extra.variant_title;
      if (extra.discount_code) body.discount_code = extra.discount_code;
      if (extra.product_id) body.product_id = extra.product_id;
      if (extra.block) body.block = extra.block;
      if (c.language) body.language = c.language;

      var json = JSON.stringify(body);
      log('track', event, body);
      if (typeof fetch === 'function') {
        fetch(c.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          mode: 'no-cors',
          body: json,
          keepalive: true
        }).catch(function () {});
      } else if (navigator.sendBeacon) {
        navigator.sendBeacon(c.endpoint, new Blob([json], { type: 'text/plain' }));
      }
    } catch (e) {}
  }

  // ---- Impressions, deduped per DRAWER SESSION -----------------------------
  // A drawer session runs from open() to close(). The drawer re-renders its whole
  // innerHTML on every /cart/change.js and /cart/add.js, so anything stored in the
  // DOM would reset constantly and every quantity tick would look like a new
  // impression. This Set lives on the module instead, and is cleared only when the
  // drawer actually opens or closes.
  var seenThisSession = {};
  var cardObserver = null;
  var listObserver = null;

  function resetDrawerSession() { seenThisSession = {}; }

  function fireImpression(card) {
    // The hidden preview printed into an empty cart (cart-drawer.liquid) is never reported.
    if (card.closest && card.closest('[data-td-preview]')) return;
    var c = ctx(card);
    if (!c) return;
    var pid = card.getAttribute('data-cu-pid') || card.getAttribute('data-cu-handle');
    if (!pid || seenThisSession[pid]) return;
    seenThisSession[pid] = true;
    var addBtn = card.querySelector('[data-cu-add]');
    track('impression', c, {
      entry: card.getAttribute('data-cu-handle') || '',
      product_id: pid,
      slot: card.getAttribute('data-cu-slot') || '',
      variant_id: addBtn ? addBtn.getAttribute('data-cu-variant') : null,
      block: card.getAttribute('data-cu-block') || ''
    });
  }

  // Only cards actually on screen count. In the slider two of five are visible, and
  // the rest should register as the shopper scrolls to them.
  function observeCards() {
    var grid = document.querySelector('.td-cart-upsell__grid');
    var cards = document.querySelectorAll('.td-cart-upsell__card');
    if (!cards.length) return;
    if (cardObserver) cardObserver.disconnect();
    if (listObserver) listObserver.disconnect();
    if (typeof IntersectionObserver !== 'function') {
      for (var i = 0; i < cards.length; i++) fireImpression(cards[i]);
      return;
    }
    var onSeen = function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) fireImpression(e.target); });
    };
    var sliderRoot = grid && grid.scrollWidth > grid.clientWidth ? grid : null;
    cardObserver = new IntersectionObserver(onSeen, { root: sliderRoot, threshold: 0.5 });
    // Accessories rows sit outside the slider, so the slider root would never see
    // them; they get a viewport observer of their own.
    listObserver = sliderRoot ? new IntersectionObserver(onSeen, { root: null, threshold: 0.5 }) : null;
    for (var j = 0; j < cards.length; j++) {
      (listObserver && !sliderRoot.contains(cards[j]) ? listObserver : cardObserver).observe(cards[j]);
    }
  }

  function trackAdd(event, card, btn, o) {
    var c = ctx(card || btn);
    if (!c) return;
    var extra = {
      entry: card ? card.getAttribute('data-cu-handle') || '' : '',
      product_id: card ? card.getAttribute('data-cu-pid') || '' : '',
      slot: btn ? btn.getAttribute('data-cu-slot') || '' : '',
      variant_id: btn ? btn.getAttribute('data-cu-variant') : null,
      variant_title: btn ? btn.getAttribute('data-cu-variant-title') || '' : '',
      discount_code: 'none', // mirrors the _upsell_discount line property
      block: card ? card.getAttribute('data-cu-block') || '' : ''
    };
    if (event === 'add_success') {
      var it = o && o.data && o.data.items && o.data.items[0];
      extra.value = it ? it.price : null;
    } else {
      // Do NOT classify on the message text — Shopify localises the availability
      // sentence per market ("Tylko 4 poz. …" on PL, "Zaradi razpoložljivosti …"
      // on SI), so any English regex silently mislabels every non-English store.
      // Both failures are 422; the language-independent tell is that Shopify keeps
      // `message: "Cart Error"` untranslated for non-inventory failures, while an
      // inventory refusal puts the localised sentence in BOTH message and
      // description.
      var err = (o && o.error) || {};
      var b = err.cartBody || {};
      var msg = String(b.message || '');
      var desc = String(b.description || '');
      if (!err.httpStatus) extra.reason = 'network_error';
      else if (msg === 'Cart Error' || desc === 'Cannot find variant') extra.reason = 'validation';
      else if (err.httpStatus === 422 && msg && msg === desc) extra.reason = 'sold_out';
      else extra.reason = 'validation';
    }
    track(event, c, extra);
  }

  // The drawer's open/close are methods, not events — watch the `active` class the
  // custom element toggles instead. Opening starts a fresh session; closing ends it.
  function watchDrawerSession() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || drawer.__tdTrackBound) return;
    drawer.__tdTrackBound = true;
    var wasActive = drawer.classList.contains('active');
    new MutationObserver(function () {
      var now = drawer.classList.contains('active');
      if (now === wasActive) return;
      wasActive = now;
      resetDrawerSession();
      if (now) setTimeout(observeCards, 250);
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    if (wasActive) setTimeout(observeCards, 250);
  }

  function startTracking() {
    watchDrawerSession();
    // Re-arm after every re-render; the dedupe Set is what stops repeat impressions.
    ['cart:updated', 'cart:refresh'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(observeCards, 120); });
    });
    var drawer = document.querySelector('cart-drawer');
    if (drawer) new MutationObserver(function () {
      if (drawer.classList.contains('active')) observeCards();
    }).observe(drawer, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startTracking);
  else startTracking();
})();
